import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Panel,
  PanelHeader,
  PanelBody,
} from "../../components/panel/panel.jsx";
import * as bootstrap from "bootstrap";
import { useActiveEvent } from "../../hooks/useEvacuation.js";
import useAuthStore from "../../stores/auth-store.js";
import Swal from "sweetalert2";

const EvacuationEventDetails = () => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const userId = user?.id || null;

  // Determine if we are on the evacuation portal page
  const location = useLocation();
  const pathName = location.pathname.split("/")[2];
  const isPortalPage = pathName === "evacuation-portal";
  console.log("🌐 EvacuationPortalSelector: isPortalPage =", isPortalPage);

  // Get active evacuation event
  const { data: eventData, isLoading: loading, isFetching, error } = useActiveEvent(userId);

  // Convert seconds to HH:MM:SS format
  const formatSecondsToTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "—";
    const sec = parseInt(seconds, 10);
    const hours = Math.floor(sec / 3600);
    const minutes = Math.floor((sec % 3600) / 60);
    const secs = sec % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:${String(secs).padStart(2, "0")}`;
  };

  // THIS SHOULD GO IN PARENT COMPONENT
  const handleNoEventAlert = () => {
    Swal.fire({
      icon: "info",
      title: "No Active Event",
      text: "There is currently no active evacuation event for your account. You will be redirected to the Evacuation Events page.",
      confirmButtonText: "OK",
    }).then(() => {
      navigate("/evacuation-portal-panels/evacuation-events/", {
        replace: true,
      });
    });
  };

  // Handle no active event
  useEffect(() => {
    // Guard 1: Check if user is authenticated
    if (!userId) {
      console.log("⚠️ User not authenticated, skipping no event alert");
      return;
    }

    // Guard 2: Only run on evacuation portal page
    if (!isPortalPage) {
      console.log(
        "⚠️ Not on evacuation portal page, skipping no event alert"
      );
      return;
    }

    // Guard 3: If not loading/fetching and no event data, show alert
    // isFetching checks for background refetch (happens during client context switch)
    if (!loading && !isFetching && !eventData && !error) {
      handleNoEventAlert();
    }
  }, [loading, isFetching, eventData, error]);

  // Popover initialization
  useEffect(() => {
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );
    const popoverInstances = [];

    if (popoverTriggerList) {
      popoverTriggerList.forEach(function (elm) {
        const popover = new bootstrap.Popover(elm);
        popoverInstances.push(popover);
      });
    }

    // Cleanup function
    return () => {
      popoverInstances.forEach((popover) => {
        try {
          popover.dispose();
        } catch (error) {
          console.warn("Error disposing popover:", error);
        }
      });

      const remainingPopovers = document.querySelectorAll(".popover");
      remainingPopovers.forEach((el) => {
        try {
          el.remove();
        } catch (error) {
          console.warn("Error removing popover element:", error);
        }
      });
    };
  }, [eventData]);

  // Loading state
  if (loading) {
    return (
      <div className="card bg-gray-900 border-0 mb-3">
        <div className="card-body p-3">
          <div className="d-flex align-items-center">
            <div
              className="spinner-border spinner-border-sm me-2"
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <small className="text-gray-400">Loading event details...</small>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="card bg-gray-900 border-0 mb-3">
        <div className="card-body p-3">
          <small className="text-danger">
            {error?.message || "No event data available"}
          </small>
        </div>
      </div>
    );
  }

  if (!eventData) {
    return (
      <div className="card bg-gray-900 border-0 mb-3">
        <div className="card-body p-3">
          <div className="alert alert-info mb-0">
            <i className="fa fa-info-circle me-2"></i>
            <small>
              No active evacuation event. Please create or activate an event
              first.
            </small>
          </div>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="d-flex flex-column p-3 gap-3">
        <Panel className="bg-gray-900 border border-gray-700 rounded-3">
          <PanelHeader
            className="border-bottom border-gray-700 rounded-top"
            noButton={true}
          >
            <div className="d-flex align-items-center gap-2">
              <i className="bi bi-broadcast-pin text-warning"></i>
              <h5 className="text-warning fw-bolder mb-0">
                Active Evacuation Event
              </h5>
              <i
                className="fa fa-info-circle fs-5"
                data-bs-toggle="popover"
                data-bs-trigger="hover"
                data-bs-title="Active Evacuation Event"
                data-bs-placement="bottom"
                data-bs-content="Information about the active evacuation event, currently taking place."
              ></i>
            </div>
          </PanelHeader>
          <PanelBody className="bg-gray-900 rounded-bottom">
            <div className="d-flex flex-column align-items-center gap-2 mb-0">
              <div className="card w-100 bg-gray-900 m-0">
                <div className="card-body">
                  {/* Card Title */}
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <i className="fa-solid fa-database fa-lg text-warning"></i>
                    <h5 className="text-warning fw-bolder mb-0">
                      Evacuation Data
                    </h5>
                  </div>

                  {/* Event Title */}
                  <div className="mb-3">
                    <h6 className="text-muted d-block mb-0">Event Title</h6>
                    <p className="text-white mb-0 fw-bold">
                      {eventData.Title || "—"}
                    </p>
                  </div>

                  {/* Event Type */}
                  <div className="mb-3">
                    <h6 className="text-muted d-block mb-0">Event Type</h6>
                    <p className="text-white mb-0 fw-bold">
                      {eventData.Event_Type_Name || "—"}
                    </p>
                  </div>

                  {/* Status & Priority Row */}
                  <div className="row">
                    <div className="col-6">
                      <h6 className="text-muted d-block mb-0">Status</h6>
                      <p className="text-white mb-0">
                        <span
                          className={`badge ${
                            eventData.Status?.toLowerCase() === "active"
                              ? "bg-success"
                              : "bg-secondary"
                          }`}
                        >
                          {eventData.Status || "—"}
                        </span>
                      </p>
                    </div>
                    <div className="col-6">
                      <h6 className="text-muted d-block mb-0">Priority</h6>
                      <p className="text-white mb-0">
                        <span
                          className={`badge ${
                            eventData.Priority === "High"
                              ? "bg-danger"
                              : eventData.Priority === "Medium"
                              ? "bg-warning"
                              : "bg-info"
                          }`}
                        >
                          {eventData.Priority || "—"}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card w-100 bg-gray-900 m-0">
                <div className="card-body">
                  {/* Card Title */}
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <i className="fa-solid fa-user fa-lg text-warning"></i>
                    <h5 className="text-warning fw-bolder mb-0">
                      Official Information
                    </h5>
                  </div>

                  {/* Official Information */}
                  <div className="mt-3 pt-3 border-top border-gray-700">
                    <div className="mb-2">
                      <h6 className="text-muted d-block mb-0">Official Name</h6>
                      <p className="text-white mb-0">
                        {eventData.Official_FName || eventData.Official_LName
                          ? `${eventData.Official_FName || ""} ${
                              eventData.Official_LName || ""
                            }`.trim()
                          : "—"}
                      </p>
                    </div>
                    <div className="mb-2">
                      <h6 className="text-muted d-block mb-0">Organization</h6>
                      <p className="text-white mb-0">
                        {eventData.Organization_ID || "—"}
                      </p>
                    </div>
                  </div>

                  {/* Event Dates & Times */}
                  <div className="mt-3 pt-3 border-top border-gray-700">
                    <div className="row mb-2">
                      <div className="col-6">
                        <h6 className="text-muted d-block mb-0">Start Date</h6>
                        <p className="text-white mb-0 text-nowrap">
                          {eventData.Start_Date || "—"}
                        </p>
                      </div>
                      <div className="col-6">
                        <h6 className="text-muted d-block mb-0">Start Time</h6>
                        <p className="text-white mb-0 text-nowrap">
                          {formatSecondsToTime(eventData.Start_Time)}
                        </p>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-6">
                        <h6 className="text-muted d-block mb-0">End Date</h6>
                        <p className="text-white mb-0 text-nowrap">
                          {eventData.End_Date || "—"}
                        </p>
                      </div>
                      <div className="col-6">
                        <h6 className="text-muted d-block mb-0">End Time</h6>
                        <p className="text-white mb-0 text-nowrap">
                          {eventData.End_Time || "—"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </PanelBody>
        </Panel>
      </div>
    </React.Fragment>
  );
};

export default EvacuationEventDetails;
